/**
 * Warum ruckelt es gerade? Der Gerätezustand an jeder `[scroll-t]`-Zeile. (04.10.2026, Build 103)
 *
 * Martin, 04.10.: Der Chat lief um 13:39 mit 16,7 ms je Bild, ab 13:49 mit 33 ms (`art=bildrate stand=halb`,
 * auch ohne zu scrollen) — dazwischen kein Eingriff von uns. Ob das Telefon bremst (Energiesparmodus, Hitze)
 * oder die Seite selbst, kann die Seite nicht lesen; die App kann es (`geraeteZustand`, Build 103).
 *
 * Hier wird der Stand alle 15 s geholt, solange die Seite sichtbar ist, und als kurzes Stück an die Zeilen
 * gehängt: ` strom=aus hitze=nominal akku=64 ladung=nein fps=120`. Ohne App (Browser) oder vor Build 103 steht
 * ` gerät=-`. Nur Zahlen und Stichwörter. Kostet einen Aufruf je 15 s, nie im Takt eines Wischs.
 */
import { geraeteZustand, type GeraeteZustandNativ } from "./native-recorder.js";

/** So alt darf der Stand sein, bevor er neu geholt wird. */
const ZUSTAND_ALTER_MS = 15_000;

/** Ein Wort ohne Leerzeichen für das Journal. */
function wort(v: unknown): string {
  const t = typeof v === "string" ? v.trim().replace(/[^A-Za-z0-9_-]/g, "") : "";
  return t || "-";
}

/** Aus der Antwort der App das Stück für die Zeile. `null` → ` gerät=-`. */
export function zustandSuffix(z: GeraeteZustandNativ | null): string {
  if (!z) return " gerät=-";
  const strom = z.energiesparmodus === true ? "ein" : z.energiesparmodus === false ? "aus" : "-";
  const akku = typeof z.akku === "number" && z.akku >= 0 ? String(Math.round(z.akku)) : "-";
  const fps = typeof z.maxFps === "number" && z.maxFps > 0 ? String(Math.round(z.maxFps)) : "-";
  return ` strom=${strom} hitze=${wort(z.hitze)} akku=${akku} ladung=${wort(z.ladung)} fps=${fps}`;
}

let stand: GeraeteZustandNativ | null = null;
let geholtUm = 0;
let laeuft = false;
let timer: ReturnType<typeof setInterval> | null = null;

async function hole(): Promise<void> {
  if (laeuft) return;
  laeuft = true;
  try {
    stand = await geraeteZustand();
    geholtUm = Date.now();
  } finally {
    laeuft = false;
  }
}

/** Das Stück für die nächste Zeile — der zuletzt geholte Stand. */
export function geraeteSuffix(): string {
  if (Date.now() - geholtUm > ZUSTAND_ALTER_MS * 2) void hole();
  return zustandSuffix(stand);
}

/** Einmal beim Start: holen und alle 15 s auffrischen, solange die Seite sichtbar ist. */
export function starteGeraeteZustand(): void {
  if (timer) return;
  void hole();
  timer = setInterval(() => {
    if (document.visibilityState === "visible") void hole();
  }, ZUSTAND_ALTER_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void hole();
  });
}
